import React, { useEffect, useState } from "react";
import {useTheme, styled} from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {grey} from "@mui/material/colors";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import OptimizationProgress from "./OptimizationProgress";
import {UserContext} from "../../App";
import {deleteData, postData} from "../../utils/api";


const getStepFinishStatusFromProgress = (progress) => {
    // Define a default array with all steps set to false (not finished)
    const defaultStatus = new Array(4).fill(false);

    if (!progress || typeof progress !== 'object') {
        return defaultStatus;
    }

    const stepToIndexMapping = {
        "initializing": [0],
        "percent_trainings_completed": [1,2],
        "is_finalizing": [1],
        "pipeline_finished": [3]
    };

    // Iterate through the progress object using Object.keys
    Object.keys(progress).forEach(stepName => {
        const indices = stepToIndexMapping[stepName];
        if (indices && Array.isArray(indices)) {
            indices.forEach(index => {
                if (typeof index !== 'undefined' && progress[stepName] === 1.0) {
                    defaultStatus[index] = true;
                }
            });
        }
    });
    return defaultStatus;
}

export default function HPOTrainingStatus(props) {

    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();
    // get experiment methods
    const {
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        fetchExperiments
    } = React.useContext(UserContext);

    // needed for routing to inspection site
    const navigate = useNavigate();
    const steps = [
        {
            label: t("trainingStatus.LabelStep1"),
            description: t("trainingStatus.DescriptionStep1"),
        },
        {
            label: t("trainingStatus.LabelStep2"),
            description: t("trainingStatus.DescriptionStep2"),
        },
        {
            label: t("trainingStatus.LabelStep3"),
            description: t("trainingStatus.DescriptionStep3"),
        },
        {
            label: t("trainingStatus.LabelStep4"),
            description: t("trainingStatus.DescriptionStep4"),
        },
    ];

    const [stepFinishStatus, setStepFinishStatus] = React.useState([
        false,
        false,
        false,
        false,
        false,
    ]);

    const [activeStep, setActiveStep] = React.useState(0);

    const handleFinish = () => {
        navigate("/analyze");
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };
    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    const handleStep = (step) => () => {
        setActiveStep(step);
    };

    /* SOCKET CONNECTION
    The socket.io connection is used, to update the processing progress
    */
    const [progressData, setProgressData] = React.useState({
        progress: {completion_percentage: 0, stepnames: []},
        status: "Initializing",
        pgpePlottingData: {},
        pgpeStats: {},
    });

    const [refreshFlag, setRefreshFlag] = useState(0);

    // connect to socket, when the current dataset is gathered
    useEffect(() => {
        props.socket.connect();
        props.socket.on("connect", () => {
            const roomData = {...props.pipelineRoom};
            roomData.runName = `${props.pipelineRoom.runName}_${props.stepName}`
            // this method will be triggered by the server
            props.socket.on("update-hpo-training", (data) => {
                // Merge the existing state with the received data so that plotting information can be sent separately
                setProgressData(prevData => {
                    const updatedProgressData = {
                        ...prevData,
                        ...data,
                        progress: {
                            ...prevData.progress,
                            ...data.progress
                        },
                    };

                    //
                    // Translate progress into finished steps based on updatedProgressData
                    const updatedFinishStatus = getStepFinishStatusFromProgress(updatedProgressData.progress);
                    setStepFinishStatus(updatedFinishStatus);
                    return updatedProgressData;
                });
            });
            props.socket.on("hpo-training-completed", () => {
                fetchExperiments({latest: props.experimentName});
            });

            // create room for current user
            props.socket.emit("create-hpo-training-room", roomData);
        });

    }, [datasets, currentDatasetIdx, experiments, currentExperimentIdx, props.stepName, refreshFlag]);

    // Function to finalize training (API call)
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const handleCancelTrainingClick = () => {
        setOpenConfirmDialog(true); // Open the confirmation dialog
    };

    const handleCloseDialog = () => {
        setOpenConfirmDialog(false); // Close the confirmation dialog
    };

    const handleConfirmCancel = async () => {
        try {
            await deleteData(
                `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${props.pipelineRoom.runName}_${props.stepName}`
            ).then(async (data) => {
                if (data.status === 200) {
                    // update the sidebar
                    props.socket.emit("refresh-runs", {
                        room: `${props.pipelineRoom.uid}-general`,
                    });
                    // close dialog
                    setOpenConfirmDialog(false);
                    // navigate to start training
                    navigate("/train");
                }
            });
        } catch (e) {
            console.log(e);
        }
    };

    const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);

    const handleFinalizeTrainingClick = () => {
        setOpenFinalizeDialog(true); // Open the confirmation dialog
    };

    const handleFinalizeDialog = () => {
        setOpenFinalizeDialog(false); // Close the confirmation dialog
    };

    const handleFinalizeTraining = async () => {
        const numRuns = progressData.pgpePlottingData?.metric?.newest?.length || null;
        try {
            postData(
                `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/run/${props.pipelineRoom.runName}_${props.stepName}/update-num-run`,
                {num_runs: numRuns}
            ).then(async (data) => {
                if (data.status === 200) {
                    console.log("Finalizing training");
                    if (data.status === 200) {
                        // close dialog
                        setOpenFinalizeDialog(false);
                    }
                    setRefreshFlag((prevFlag) => prevFlag + 1);
                }
            });
        } catch (error) {
            console.error("Error finalizing training", error);
        }
    };

    // Step Details
    const DetailsAccordion = styled(Accordion)(() => ({
        backgroundColor: 'transparent',
        boxShadow: theme.shadows[0],
        '&.Mui-expanded': {
            backgroundColor: 'transparent',
        },
        '&.MuiDetailsAccordionSummary': {
            flexDirection: 'row-reverse'
        },
    }));

    const DetailsAccordionSummary = styled(AccordionSummary)(() => ({

        flexDirection: 'row-reverse'
    }));


    return (
        <Box id="processing-status-container" sx={{padding: theme.spacing(4, 1)}}>
            <Typography variant="h4" gutterBottom>
                {t("trainingStatus.PageTitle")}
                <br/>
            </Typography>
            <Typography variant="body2">
                {t("trainingStatus.PageDescription")}
            </Typography>
            <br/>

            <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="start"
            >
                <Grid item xl={9} xs={12} sx={{margin: theme.spacing(0, 4)}}>
                    <Typography variant="h6" sx={{margin: theme.spacing(2, 0)}}>
                        {t("trainingStatus.PipelineProgressTitle")}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progressData.progress.completion_percentage}
                        color="success"
                    />
                    <Typography variant="body2" color="text.secondary">
                        {`${Math.round(progressData.progress.completion_percentage)}%`}
                    </Typography>
                    {/* Example rendering of run_status */}
                    <Typography variant="subtitle1" color="text.secondary">
                        {`${t("trainingStatus.StatusTitle")} ${t(`trainingStatus.${progressData.status}`)}`}
                    </Typography>
                    {/* Button Container */}
                    {stepFinishStatus[2] === false && (
                        <Grid container spacing={2} sx={{ marginTop: theme.spacing(-2), marginBottom : theme.spacing(-2) }} justifyContent="space-between">
                            {/* Cancel Training Button */}
                            <Grid item xs={4}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            fullWidth
                                            onClick={handleCancelTrainingClick}
                                        >
                                            {t("trainingStatus.CancelTrainingButton")}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title={t("trainingStatus.HintCancelTrainingButton")}>
                                            <IconButton>
                                                <InfoIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Extend Training Button */}
                            <Grid item xs={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    sx={{ display: 'none' }}  // Hides the button
                                    onClick={handleFinalizeTraining}
                                >
                                    {t("trainingStatus.ExtendTraining")}
                                </Button>
                            </Grid>

                            {/* Finalize Training Button */}
                            <Grid item xs={4}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            sx={{ display: stepFinishStatus[1] || refreshFlag>0 ? 'none' : 'block' }}
                                            onClick={handleFinalizeTrainingClick}
                                        >
                                            {t("trainingStatus.FinalizeTrainingButton")}
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        <Tooltip title={t("trainingStatus.HintFinalizeTrainingButton")}>
                                            <IconButton>
                                                <InfoIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                {/* Confirmation Dialog */}
                <Dialog open={openConfirmDialog} onClose={handleCloseDialog}>
                    <DialogTitle>{t("trainingStatus.CancelConfirmationTitle")}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {t("trainingStatus.CancelConfirmationMessage")}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="primary">
                            {t("trainingStatus.CancelButton")}
                        </Button>
                        <Button onClick={handleConfirmCancel} color="secondary">
                            {t("trainingStatus.ConfirmCancelButton")}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={openFinalizeDialog} onClose={handleFinalizeDialog}>
                    <DialogTitle>{t("trainingStatus.FinalizeConfirmationTitle")}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {t("trainingStatus.FinalizeConfirmationMessage")}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleFinalizeDialog} color="primary">
                            {t("trainingStatus.FinalizeButton")}
                        </Button>
                        <Button onClick={handleFinalizeTraining} color="secondary">
                            {t("trainingStatus.ConfirmFinalizeButton")}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Grid item xl={12} xs={12}>
                    <DetailsAccordion defaultExpanded>
                        <DetailsAccordionSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="details1-content"
                            id="details1-header"
                        >
                        <Typography variant="h7" color="text.secondary">
                            {t("trainingStatus.TrainingStepDescriptions")}
                        </Typography>
                        </DetailsAccordionSummary>
                        <AccordionDetails>
                            <Grid item lg={6} xs={12}>
                                <Stepper
                                    nonLinear
                                    activeStep={activeStep}
                                    orientation="vertical"
                                    sx={{marginTop: theme.spacing(0)}}
                                >
                                    {steps.map((step, index) => (
                                        <Step
                                            key={step.label}
                                            sx={{
                                                // overwrite default step colors, so the color is only dependet on whether the status is finished or not
                                                "& .MuiStepIcon-root.Mui-active": stepFinishStatus.at(index)
                                                    ? {color: theme.palette.success.dark}
                                                    : {color: grey[300]},
                                                "& .MuiStepIcon-root": stepFinishStatus.at(index)
                                                    ? {color: theme.palette.success.main}
                                                    : {color: grey[300]},
                                            }}
                                        >
                                            <StepButton onClick={handleStep(index)}>
                                                {step.label} {stepFinishStatus.at(index) ? "✅" : ""}
                                            </StepButton>
                                            <StepContent>
                                                <Typography>{step.description}</Typography>
                                                <Box sx={{mb: 2}}>
                                                    <div>
                                                        {
                                                            index !== steps.length - 1 ? (
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handleNext}
                                                                    sx={{mt: 1, mr: 1}}
                                                                    color="primary"
                                                                >
                                                                    {t("trainingStatus.ReadNextButton")}
                                                                </Button>
                                                            ) : (
                                                                ""
                                                            ) // don't show "Read Next" Button on last step
                                                        }
                                                        <Button
                                                            disabled={index === 0}
                                                            onClick={handleBack}
                                                            sx={{mt: 1, mr: 1}}
                                                        >
                                                            {t("trainingStatus.BackButton")}
                                                        </Button>
                                                    </div>
                                                </Box>
                                            </StepContent>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Grid>
                        </AccordionDetails>
                    </DetailsAccordion>
                    {progressData.hyperparameterRanges && Object.keys(progressData.hyperparameterRanges).length > 0 && (
                        <DetailsAccordion>
                            <DetailsAccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="details0-content"
                                id="details0-header"
                            >
                                <Typography variant="h7" color="text.secondary">
                                    {t("trainingStatus.SelectedHyperparameterRanges")}
                                </Typography>
                            </DetailsAccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} sx={{ mt: 2 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><b>Parameter</b></TableCell>
                                                <TableCell><b>Min</b></TableCell>
                                                <TableCell><b>Max</b></TableCell>
                                                <TableCell><b>Init</b></TableCell>
                                                <TableCell><b>Optimize</b></TableCell>
                                                <TableCell><b>Type</b></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(progressData.hyperparameterRanges || {}).map(([category, params]) =>
                                                Object.entries(params).map(([param, details]) => (
                                                    <TableRow key={`${category}-${param}`}>
                                                        <TableCell>{param}</TableCell>
                                                        <TableCell>{details.optimize ? details.min : "-"}</TableCell>
                                                        <TableCell>{details.optimize ? details.max : "-"}</TableCell>
                                                        <TableCell>{details.init}</TableCell>
                                                        <TableCell>{details.optimize ? "✅" : "❌"}</TableCell>
                                                        <TableCell>{details.type}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </DetailsAccordion>
                    )}
                    <DetailsAccordion defaultExpanded>
                        <DetailsAccordionSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="details2-content"
                            id="details2-header"
                        >
                            {t("trainingStatus.TrainingPlotsTitle")}
                        </DetailsAccordionSummary>
                        <AccordionDetails>

                            <Grid item lg={12} xs={12} sx={{minWidth: "800px"}}>
                                <Divider/>
                                <OptimizationProgress plotData={progressData.pgpePlottingData}
                                                      statsData={progressData.pgpeStats}/>
                            </Grid>
                        </AccordionDetails>
                    </DetailsAccordion>
                    {progressData.progress.completion_percentage === 100 ? (
                        <Paper
                            square
                            elevation={0}
                            sx={{p: 3, marginTop: theme.spacing(3), background: grey[100]}}
                        >
                            <Typography>{t("trainingStatus.CompletedMessage")}</Typography>
                            <Button onClick={handleFinish} sx={{mt: 1, mr: 1}} variant="contained"
                                    color="primary">
                                {t("trainingStatus.ProceedButton")}
                            </Button>
                        </Paper>
                    ) : ""}
                </Grid>
            </Grid>
        </Box>
    );
}
